import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { useMe, type MeSport } from "@/lib/me";
import { signOut } from "@/lib/auth-client";
import LanguageSwitcher from "@/components/language-switcher";
import ClubInfoStep from "./club-info-step";
import CourtsStep from "./courts-step";

export default function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, club, isLoading, refresh } = useMe();
  const [step, setStep] = useState<1 | 2>(1);
  // Sports created in step 1 — needed in step 2 to assign courts.
  const [sports, setSports] = useState<MeSport[]>([]);

  if (isLoading) {
    return (
      <div className="min-h-svh flex items-center justify-center bg-neutral-50">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "MANAGER" && user.role !== "SUPERADMIN")
    return <Navigate to="/" replace />;
  if (!club) return <Navigate to="/" replace />;
  if (club.setupComplete) return <Navigate to="/" replace />;

  async function handleStep1Done(createdSports: MeSport[]) {
    // Refresh so the latest club info + sports are reflected if the user
    // clicks Back into step 1.
    await refresh();
    setSports(createdSports);
    setStep(2);
  }

  async function handleFinish() {
    await refresh();
    toast.success(t("onboarding.successToast"));
    navigate("/", { replace: true });
  }

  async function handleLogout() {
    await signOut({ fetchOptions: { onSuccess: () => navigate("/login") } });
  }

  return (
    <div className="min-h-svh bg-neutral-50 py-6 sm:py-10 px-4">
      <div className="max-w-2xl mx-auto mb-4 flex justify-end items-center gap-2">
        <LanguageSwitcher />
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800 transition-colors cursor-pointer px-2 py-1 rounded-md hover:bg-neutral-100"
        >
          <LogOut className="w-4 h-4" />
          {t("nav.logout")}
        </button>
      </div>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">
            {club.name}
          </div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800">
            {t("onboarding.title")}
          </h1>
          <p className="text-sm text-neutral-500">{t("onboarding.subtitle")}</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
              step === 1
                ? "bg-primary-600 text-white"
                : "bg-primary-100 text-primary-700"
            }`}
          >
            <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">
              1
            </span>
            {t("onboarding.step1")}
          </div>
          <div className="w-8 h-px bg-neutral-200" />
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
              step === 2
                ? "bg-primary-600 text-white"
                : "bg-neutral-100 text-neutral-500"
            }`}
          >
            <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">
              2
            </span>
            {t("onboarding.step2")}
          </div>
        </div>

        {/* Step content */}
        {step === 1 ? (
          <ClubInfoStep club={club} onDone={handleStep1Done} />
        ) : (
          <CourtsStep
            sports={sports}
            clubWorkingHoursStart={club.workingHoursStart}
            clubWorkingHoursEnd={club.workingHoursEnd}
            maxCourts={club.maxCourts}
            onFinish={handleFinish}
            onBack={() => setStep(1)}
          />
        )}
      </div>
    </div>
  );
}
